import React from 'react';

import cn from 'classnames';
import ConfirmationDialog from '../../../shared/components/modals/ConfirmationDialog';
import date from '../../../shared/util/utilities/date';
import ExternalLink from '../../../shared/components/ExternalLink';
import internalApiClient from '../../../shared/util/api/internalApiClient';
import PackageCards from '../../settings/company/subscription/PackageCards';
import { Button, Dialog, useToast } from '@economic/taco';
import { ChangeSubscriptionDetails } from '../../settings/company/subscription/types';
import { t, Trans } from '@lingui/macro';
import { UpgradePlanInfo } from './shared';
import { useCurrentAgreement } from '@economic/react-app';
import { useNavigate, useLocation } from 'react-router-dom';

interface Props {
    data: UpgradePlanInfo;
    subscriptionDetails: ChangeSubscriptionDetails;
    hide: () => void;
}

export default function ChoosePlanPage({ data, subscriptionDetails, hide }: Props) {
    const toast = useToast();
    const agreement = useCurrentAgreement();
    const location = useLocation();
    const navigate = useNavigate();

    const handleTrialExtension = async () => {
        try {
            const response = await internalApiClient.post(`/company/extendTrial`);
            hide();
            window.top?.refreshRouting();
            toast.success(t`Trial extended until ${date.format(date.parseFromISOString(response.data?.expiryDate))}`);

            if (location.pathname === '/home') {
                // When on /home we need to reload in order for the UpgradeWidget to rerender
                navigate('/', { replace: true });
            }
        } catch (error) {
            toast.error(t`An error occurred.`);
        }
    };

    const { subscriptionTermsOnclick, isPlanChangeNotificationVisible, planChangeNotificationHtml } = data;

    const isTrialExpired = agreement.subscription.trial?.expiryDate
        ? new Date(agreement.subscription.trial.expiryDate).getTime() < Date.now()
        : false;

    return (
        <div className={cn('plan-upgrade-dialog', { 'relative bg-white': isTrialExpired })}>
            <h1>
                <Trans>Upgrade below to continue using e-conomic.</Trans>
            </h1>
            <div className="flex flex-row justify-between items-center">
                <div>
                    <Trans>We have of course kept all the data you have entered in the trial period.</Trans>

                    {agreement.subscription.trial?.canExtendTrial && (
                        <span className="ml-2">
                            <Trans>You can also extend your demo.</Trans>
                            <Button
                                className="ml-4"
                                dialog={props => (
                                    <Dialog {...props}>
                                        <Dialog.Content aria-label={t`Extend your trial?`}>
                                            {({ close }) => (
                                                <ConfirmationDialog
                                                    label={t`Extend your trial?`}
                                                    message={t`Do you want to extend your trial?`}
                                                    onCancel={close}
                                                    onConfirm={handleTrialExtension}
                                                />
                                            )}
                                        </Dialog.Content>
                                    </Dialog>
                                )}>
                                <Trans>Extend</Trans>
                            </Button>
                        </span>
                    )}
                </div>
            </div>

            <div className="flex plans justify-center">
                <PackageCards changeSubscriptionDetails={subscriptionDetails} isTrialUpgrade />
            </div>
            <div className="leading-8 italic">
                <Trans>
                    Read more on{' '}
                    <ExternalLink href="https://www.e-conomic.dk/priser">
                        <Trans>our pricing page</Trans>
                    </ExternalLink>{' '}
                    or view <ExternalLink href={subscriptionTermsOnclick}>our subscription terms</ExternalLink>.
                </Trans>
            </div>

            <div className="linksAndPlanChangeContainer flex">
                {/* This code is going away in end of Dec (2019) ref ticket EC-24592 */}
                {isPlanChangeNotificationVisible && (
                    <>
                        <div className="flex bg-grey p-1">
                            <div>
                                <span className="p-px bg-blue text-white mx-1 my-0 font-bold rounded text-center inline-block w-8">
                                    !
                                </span>
                            </div>
                            <div
                                className="plans__price_change_notification_snackbar__text"
                                dangerouslySetInnerHTML={{ __html: planChangeNotificationHtml }}></div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
